import { assert } from "@ember/debug";

/**
 * There are 3 ways to construct a Timing, with parameters, with named parameters or with a string
 * @example
 * new Timing(1, 1, "d", "MORN") // Every morning
 * new Timing(1, 1, "d", ["EVE", "MORN"]) // Every morning and evening
 * new Timing(3, 1, "wk") // Three times a week.
 * new Timing(1, 2, "mo") // Once every two month
 * new Timing("1-1-d-EVE-MORN") // Every morning and evening
 */
export interface TimingPayload {
  frequency: number;
  period: number;
  periodUnit: string | undefined;
  when?: Array<string>;
}

/**
 * It creates an id from the attributes, but it is not really relevant.
 * In fact, "3 times a day" is not equal to "matin midi et soir".
 * And once by month is not equal to once every 4 weeks.
 * So take care of the way you represent the timing.
 */
export default class Timing {
  frequency: number;
  period: number;

  /**
   * You can find the different unit here : https://www.hl7.org/fhir/valueset-units-of-time.html
   */
  periodUnit: string | undefined;
  /**
   * You can find the different unit here : https://www.hl7.org/fhir/valueset-event-timing.html
   */
  when?: string[];

  static fromComputedId(computedId: string): Timing {
    const attributes = computedId.split("-");
    assert(
      `The computedId '${computedId} doesn't respect the format.`,
      attributes.length >= 2
    );

    const timing = new Timing({
      frequency: +attributes[0],
      period: +attributes[1],
      periodUnit: attributes[2],
      when: []
    });
    for (let i = 3; i < attributes.length; i++) {
      timing.when?.push(attributes[i]);
    }

    return timing;
  }

  constructor({ frequency, period, periodUnit, when }: TimingPayload) {
    this.frequency = frequency;
    this.period = period;
    this.periodUnit = periodUnit;
    // To keep consistency between different timings we have to order the `when` array
    if (when) {
      this.when = when.sort();
    }
  }

  isEqual(other: Timing | undefined): boolean {
    return this.computedId === other?.computedId;
  }

  get jsonPayload(): TimingPayload {
    const payload: TimingPayload = {
      frequency: this.frequency,
      period: this.period,
      periodUnit: this.periodUnit
    };

    if (this.when) {
      payload.when = this.when;
    }

    return payload;
  }

  /**
   * Compute an id based on the timing attributes.
   */
  get computedId(): string {
    let strId = `${this.frequency || 0}-${this.period || 0}-${
      this.periodUnit || ""
    }`;
    this.when?.forEach(function (str) {
      strId += "-" + str;
    });
    return strId.toLowerCase();
  }

  get description(): string {
    return this.toString();
  }

  toString(): string {
    return (
      this._frequencyString +
      this._periodString +
      this._periodUnitString +
      this._whenString
    );
  }

  private get _frequencyString(): string {
    return this.frequency ? `${this.frequency} fois ` : "";
  }

  private get _periodString(): string {
    if (this.period === 1) {
      return "par ";
    } else if (this.period > 1) {
      return "chaque ";
    }
    return "";
  }

  private get _periodUnitString(): string {
    switch (this.periodUnit) {
      case "d":
        return "jour ";
      case "wk":
        return "semaine ";
      case "mo":
        return "mois ";
      default:
        return "";
    }
  }

  private get _whenString(): string {
    if (!this.when) return "";

    function getTranslation(code) {
      switch (code) {
        case "MORN":
          return "le matin";
        case "EVE":
          return "le soir";
        case "CD":
          return "le midi";
        default:
          return "";
      }
    }

    let str = "";

    for (let i = 0; i < this.when.length; i++) {
      str += getTranslation(this.when[i]);

      if (i == this.when.length - 2) {
        str += " et ";
      } else if (i < this.when.length - 2) {
        str += ", ";
      }
    }

    return str;
  }
}
